import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost } from '../client';
import { qk } from './keys';
import type {
  ColleagueNearby,
  Company,
  CompanyCreateInput,
  Employee,
  EmployerPayout,
  PaginatedResponse,
  PaymentIntent,
} from '../types';
import { withQuery } from '@/utils/url';

export function useCompany() {
  return useQuery({
    queryKey: qk.company,
    queryFn: async () => {
      try {
        return await apiGet<Company>('/employer/company');
      } catch {
        return null;
      }
    },
  });
}

export function useCreateCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CompanyCreateInput) => apiPost<Company>('/employer/companies', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.company });
      qc.invalidateQueries({ queryKey: qk.me });
    },
  });
}

export type EmployeeListParams = {
  status?: string;
  cursor?: string;
  [k: string]: string | undefined;
};

export function useEmployees(params: EmployeeListParams = {}) {
  return useQuery({
    queryKey: qk.employees(params),
    queryFn: () =>
      apiGet<PaginatedResponse<Employee>>(withQuery('/employer/employees', params)),
  });
}

export function useEmployee(id: string | null) {
  return useQuery({
    queryKey: qk.employee(id ?? ''),
    queryFn: () => apiGet<Employee>(`/employer/employees/${id ?? ''}`),
    enabled: !!id,
  });
}

export function useInviteEmployee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { email: string; given_name?: string; surname?: string }) =>
      apiPost<{ id: string; status: string }>('/employer/employees/invite', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['employer', 'employees'] });
    },
  });
}

export function useVerifyEmployee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiPost<Employee>(`/employer/employees/${id}/verify`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['employer', 'employees'] });
    },
  });
}

export function useRemoveEmployee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiPost<void>(`/employer/employees/${id}/remove`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['employer', 'employees'] });
    },
  });
}

export function useTopupEmployee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, amountCents, note }: { id: string; amountCents: number; note?: string }) =>
      apiPost<PaymentIntent>(`/employer/employees/${id}/topup`, {
        amount_cents: amountCents,
        note,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['employer', 'employees'] });
      qc.invalidateQueries({ queryKey: ['employer', 'payouts'] });
    },
  });
}

export function useColleaguesNearby(enabled = true) {
  return useQuery({
    queryKey: ['employer', 'colleagues-nearby'] as const,
    queryFn: async () => {
      try {
        return await apiGet<ColleagueNearby[]>('/me/employer/colleagues-nearby');
      } catch {
        // Endpoint is employer-only; non-employer users get 403.
        // Swallow to avoid noisy UI — page already guards by role.
        return null;
      }
    },
    enabled,
  });
}

export function usePayouts(params: { cursor?: string; employee_id?: string } = {}) {
  return useQuery({
    queryKey: qk.payouts(params),
    queryFn: () =>
      apiGet<PaginatedResponse<EmployerPayout>>(withQuery('/employer/payouts', params)),
  });
}
